import {
  put, select, call, all,
} from 'lib/redux';
import i18n from 'i18n';
import * as types from 'constants/ActionTypes';

import { getInitParametersSaga, getBillingInformationSaga } from 'features/userData/UserData.saga';
import {
  getPaymentTokenInfo,
  validatePaymentInfo,
  validatePaymentInfoForBanner,
} from 'commonComponents/paymentInformation/PaymentInformation.util';
import { PAYMENT_METHOD_OPTION_IDS, ACH_SAVED_ROUTING_MASK } from 'commonComponents/paymentInformation/PaymentInformationConstants'; // #genai
import updateBillPayerInfoService from 'services/updateBillPayerInfo.service';
import bankAccountInfoService from 'services/bankAccountInfo.service'; // #genai
import updateBankAccountInfoService from 'services/updateBankAccountInfo.service'; // #genai
import updateAddressInfoService from 'services/updateAddressInfo.service';
import getPaymentMethodsService from 'services/getPaymentMethods.service'; // #genai
import setPaymentMethodDefaultService from 'services/setPaymentMethodDefault.service'; // #genai
import removePaymentMethodService from 'services/removePaymentMethod.service'; // #genai
import {
  CREDIT_CARD_NAMES, ADDRESS_TYPES, USER_EVENTS, BillingType,
  CLIENT_ERROR_RESPONSES,
} from 'constants/AppConstants';
import { logError } from 'utils/logger.utils';
import { isAchEligibleSelector } from 'utils/appSelectors.utils'; // #genai

import { trackUserEvent, scrollToTop } from 'utils/app.utils';
import {
  updatePaymentInfo,
  updateCreditCardConsent,
  updateAchConsent,
  loadAddressInfo,
  resetBillingInfoPage,
  loadBillingInfoStart,
  loadBillingInfoComplete,
  loadBillingInfoError,
  updatePaymentInfoErrors,
  updateAddressValidationErrorsForDisplay,
  updateAddressInfoErrors, // #genai Phase 2 required-field guard dispatches per-field errors
  resetPaymentToken,
  paymentTokenSuccess,
  paymentTokenError,
  submitBillingInfoStart,
  submitBillingInfoComplete,
  submitBillingInfoError,
  updatePaymentInfoErrorsForBanner,
  updateAddressInfoFields,
  loadAchInfo,
  resetBillingSubmission,
  submitBillingInfoValidationError,
  updateCardPaymentInfo,
  updateAchPaymentInfo,
  setSelectedPaymentMethod,
  updateCardPaymentInfoErrors,
  updateAchPaymentInfoErrors,
  updateCardPaymentInfoErrorsForBanner,
  updateAchPaymentInfoErrorsForBanner,
  loadPaymentMethodsStart,
  loadPaymentMethodsSuccess,
  loadPaymentMethodsError,
  setPaymentMethodDefaultSuccess,
  setPaymentMethodDefaultError,
  removePaymentMethodSuccess,
  removePaymentMethodError,
  removePaymentMethodBlockedDefault,
} from './BillingInformation.actions';
import {
  addressSelector,
  paymentInfoSelector,
  addressInfoSelector,
  getCardDataFromPaymentInfo,
  getPhase2CardRequestBody, // #genai Phase 2/2D Add-Card body (§2.6)
  getBankAccountInfoRequestBody,
  getPhase2AddressValidationErrors, // #genai Phase 2 Add-payment required-field guard
} from './BillingInformation.util';

// #genai Loads ACH bank-account info for legacy ACH-only flow (non-toggle path removed for eligible users).
export function* loadAchInfoSaga(basePaymentInfo) {
  const isAchEligible = yield select(isAchEligibleSelector);
  if (!isAchEligible) {
    yield put(loadAchInfo({}));
    return null;
  }

  const bankAccountResponse = yield call(bankAccountInfoService);
  if (!bankAccountResponse.ok) {
    yield put(loadAchInfo({}));
    return null;
  }

  const achInfo = bankAccountResponse.json || {};
  yield put(loadAchInfo(achInfo));

  if (!achInfo?.accountSuffix) return null;

  if (typeof achInfo.consent === 'boolean') {
    yield put(updateAchConsent(achInfo.consent));
  }

  return {
    firstName: achInfo.firstName || basePaymentInfo.firstName,
    lastName: achInfo.lastName || basePaymentInfo.lastName,
    accountType: achInfo.accountType || '',
    routingNumber: ACH_SAVED_ROUTING_MASK,
    accountNumber: achInfo.accountSuffix,
    accountNumberConfirm: achInfo.accountSuffix,
  };
}

// #genai Hydrates decoupled card/ACH slices and initial toggle for ACH-eligible TERMS users.
function* loadToggleUserPaymentDataSaga(billPayerInfoResponse) {
  const bankAccountResponse = yield call(bankAccountInfoService);
  const achInfo = bankAccountResponse.ok ? (bankAccountResponse.json || {}) : {};
  yield put(loadAchInfo(achInfo));

  const cardPaymentInfo = {
    firstName: billPayerInfoResponse.creditCard.cardFirstName || '',
    lastName: billPayerInfoResponse.creditCard.cardLastName || '',
    cardNumber: billPayerInfoResponse.creditCard.creditCardNumber || '',
    savedCardNumber: billPayerInfoResponse.creditCard.creditCardNumber || '',
    cardType: billPayerInfoResponse.creditCard.creditCardType || '',
    expirationDateMonth: billPayerInfoResponse.creditCard.cardExpMonth || '',
    expirationDateYear: billPayerInfoResponse.creditCard.cardExpYear || '',
    isUpdated: false,
  };

  const achPaymentInfo = {
    firstName: achInfo.firstName || '',
    lastName: achInfo.lastName || '',
    accountType: achInfo.accountType || '',
    routingNumber: achInfo.accountSuffix ? ACH_SAVED_ROUTING_MASK : '',
    accountNumber: achInfo.accountSuffix || '',
    accountNumberConfirm: achInfo.accountSuffix || '',
    isUpdated: false,
  };

  if (typeof achInfo.consent === 'boolean') {
    yield put(updateAchConsent(achInfo.consent));
  }

  // #genai Default selection rules:
  // - Both card and ACH on file → honour isACHDefault from the backend
  // - Only card on file → Card
  // - Only ACH on file → ACH
  // - Neither on file → ACH (new toggle users land on the ACH form first)
  const hasCard = Boolean(billPayerInfoResponse.creditCard?.creditCardNumber);
  const hasAch = Boolean(achInfo.accountSuffix);
  let selectedPaymentMethod;
  if (hasCard && hasAch) {
    selectedPaymentMethod = achInfo.isACHDefault === true
      ? PAYMENT_METHOD_OPTION_IDS.Ach
      : PAYMENT_METHOD_OPTION_IDS.Card;
  } else if (hasCard) {
    selectedPaymentMethod = PAYMENT_METHOD_OPTION_IDS.Card;
  } else {
    selectedPaymentMethod = PAYMENT_METHOD_OPTION_IDS.Ach;
  }

  yield put(updateCardPaymentInfo(cardPaymentInfo));
  yield put(updateAchPaymentInfo(achPaymentInfo));
  yield put(setSelectedPaymentMethod(selectedPaymentMethod, true));

  return selectedPaymentMethod;
}

export function* loadBillingInformationPageSaga() {
  yield put(resetBillingInfoPage());
  yield put(loadBillingInfoStart());
  try {
    yield getInitParametersSaga();
    yield getBillingInformationSaga();
    const { initParameters: isInitParamInError, billingInformation: isBillingInError } = yield select(({ userData }) => (userData.error));
    if (isInitParamInError || isBillingInError) throw new Error('Error fetching billing information');
    const billPayerInfoResponse = yield select(({ userData }) => (userData.billingInformation));
    const addressInfo = addressInfoSelector(billPayerInfoResponse.address);
    yield put(loadAddressInfo(addressInfo));
    const isPaymentInfoUpdated = yield select(({ billingInformation }) => (billingInformation.paymentInfo.isUpdated));
    const isAchEligible = yield select(isAchEligibleSelector);
    const paymentInfo = {
      paymentMethod: isAchEligible ? PAYMENT_METHOD_OPTION_IDS.Ach : PAYMENT_METHOD_OPTION_IDS.Card, // #genai
      firstName: billPayerInfoResponse.creditCard.cardFirstName,
      lastName: billPayerInfoResponse.creditCard.cardLastName,
      cardNumber: billPayerInfoResponse.creditCard.creditCardNumber,
      savedCardNumber: billPayerInfoResponse.creditCard.creditCardNumber,
      cardType: billPayerInfoResponse.creditCard.creditCardType,
      expirationDateMonth: billPayerInfoResponse.creditCard.cardExpMonth,
      expirationDateYear: billPayerInfoResponse.creditCard.cardExpYear,
    };
    const initialCcConsent = billPayerInfoResponse.creditCard.ccConsent;

    if (isAchEligible) {
      // #genai Toggle users: populate decoupled slices from bill-payer-info + bank-account-info.
      yield call(loadToggleUserPaymentDataSaga, billPayerInfoResponse);
    } else {
      const achOverrides = yield call(loadAchInfoSaga, paymentInfo);
      const initialPaymentInfo = achOverrides ? { ...paymentInfo, ...achOverrides } : { ...paymentInfo };
      if (!isPaymentInfoUpdated) yield put(updatePaymentInfo({ ...initialPaymentInfo, isUpdated: false }));
    }
    yield put(updateCreditCardConsent(initialCcConsent));
    yield put(loadBillingInfoComplete());
  } catch (error) {
    logError({
      message: 'Error in loadBillingInformationPageSaga',
      error: error.message,
      errorCode: error.errorCode,
    });
    yield put(loadBillingInfoError());
  }
}

// #genai Phase 2 — hydrate account context (initParameters + billingInformation + billPayerId) so
// the dedicated Add-Card / Add-ACH deep-link routes have billPayerId available for the save body.
// Unlike loadBillingInformationPageSaga, this does NOT reset the page or pre-fill any form slice,
// so the intentionally-empty add form (Figma 885:10004) is preserved. getBillingInformationSaga /
// getInitParametersSaga are idempotent (skip the fetch when userData is already populated).
//
// #genai bank-account-info fetch: /bill-payer-info does NOT return billPayerId — Phase 1 sources
// it from a separate GET /bank-account-info in loadToggleUserPaymentDataSaga (which also writes
// the full response — accountSuffix, isACHDefault, firstName/lastName — into
// billingInformation.achInfo to drive summary-mode rendering on the /billing-information page).
// Deep-linking to /add-ach or /add-card bypassed that call, so the save PUT went out with
// billPayerId undefined and the backend rejected with errorCode 15828 ("billPayerId is required
// and must be > 0.").
//
// Merge ONLY billPayerId onto userData.billingInformation. Do NOT dispatch loadAchInfo here —
// that would repopulate billingInformation.achInfo (accountSuffix, isACHDefault, ...) after
// resetAddPaymentFormState wiped it, flipping AchPaymentInformation into summary mode
// (masked routing/account fields) and clobbering the mount-time updateAchDefault(true). The save
// path already reads billingInformationSnapshot?.billPayerId as a fallback, and the component's
// !billPayerId re-fetch guard reads from userData.billingInformation.billPayerId — so this is
// where the value needs to land. Guarded by the same selector to stay idempotent across remounts.
export function* loadBillingAccountContextSaga() {
  try {
    yield getInitParametersSaga();
    yield getBillingInformationSaga();

    const currentBillPayerId = yield select(({ userData }) => userData.billingInformation?.billPayerId);
    if (!currentBillPayerId) {
      const bankAccountResponse = yield call(bankAccountInfoService);
      if (bankAccountResponse.ok && bankAccountResponse.json?.billPayerId) {
        yield put({
          type: types.SET_USER_DATA_BILL_PAYER_ID,
          billPayerId: bankAccountResponse.json.billPayerId,
        });
      }
    }
  } catch (error) {
    logError({ message: 'Error in loadBillingAccountContextSaga', error: error.message });
  }
}

export function* updatePaymentTokenInfo(paymentInfo, ccConsent) {
  yield put(resetPaymentToken());

  try {
    const {
      token,
      maskedPan,
      instrumentIdentifierId,
    } = yield call(
      getPaymentTokenInfo,
      paymentInfo.cardNumber,
      paymentInfo.cardType,
      paymentInfo.expirationDateMonth,
      paymentInfo.expirationDateYear,
    );

    const cardData = {
      cardNumber: '',
      creditCardId: null,
      paymentToken: token,
      cardName: CREDIT_CARD_NAMES[paymentInfo.cardType],
      expMonth: paymentInfo.expirationDateMonth,
      expYear: paymentInfo.expirationDateYear,
      firstName: paymentInfo.firstName,
      lastName: paymentInfo.lastName,
      secureCardNumber: '',
      maskedPan,
      instrumentIdentifierId,
    };
    yield put(paymentTokenSuccess(cardData, ccConsent));
  } catch (error) {
    logError({
      message: 'Error in updatePaymentTokenInfo',
      error: error.message,
      errorCode: error.errorCode,
      paymentInfo,
      ccConsent,
    });
    yield put(paymentTokenError(error));
    throw error;
  }
}

// #genai Picks the consent validation-banner message. Card uses its own copy; ACH differs by phase
// because the Phase 2 Add-Bank page relabels the checkbox to "I have read and agreed to Terms &
// Conditions" (Figma 269:21936) — so its banner line must read "Terms & Conditions is required"
// instead of Phase 1's "Save your bank account for future purchases is required".
const getConsentBannerKey = (isAch, isPhase2Ach) => {
  if (!isAch) return 'payment_information__expiration_card_consent_required';
  return isPhase2Ach
    ? 'payment_information__ach_consent_required_phase2'
    : 'payment_information__ach_consent_required';
};

// #genai Field-level (inline) consent message. Banner stays field-specific via getConsentBannerKey
// (Phase 2 ACH: "Terms & Conditions is required"). Inline subtext is always the generic
// "This field is required" (Phase 2 Add ACH / Add Card UX feedback).
const getConsentFieldKey = () => 'payment_information__card_fields_error';

// #genai Validates payment info for toggle users and dispatches to decoupled error slices.
function* validateAndDispatchTogglePaymentErrors(activePaymentInfo, consent, isAch, transientTokenData, t) {
  const paymentInfoForValidation = {
    ...activePaymentInfo,
    paymentMethod: isAch ? PAYMENT_METHOD_OPTION_IDS.Ach : PAYMENT_METHOD_OPTION_IDS.Card,
  };

  const errors = validatePaymentInfo(paymentInfoForValidation, t, transientTokenData || {});
  // #genai Inline consent subtext is always generic "This field is required"; banner stays specific.
  errors.ccConsent = consent ? '' : t(getConsentFieldKey());
  if (!consent) errors.isError = true;
  const isFieldValid = !errors.isError;
  delete errors.isError;

  const bannerErrors = validatePaymentInfoForBanner(paymentInfoForValidation, t, transientTokenData || {});
  // #genai ACH consent banner copy varies by phase (Terms & Conditions in Phase 2); card keeps its own.
  const consentBannerKey = getConsentBannerKey(isAch, transientTokenData?.isPhase2Ach);
  bannerErrors.ccConsent = consent ? '' : t(consentBannerKey);
  if (!consent) bannerErrors.isError = true;
  const isBannerValid = !bannerErrors.isError;
  delete bannerErrors.isError;

  if (isAch) {
    yield put(updateAchPaymentInfoErrors(errors));
    yield put(updateAchPaymentInfoErrorsForBanner(bannerErrors));
  } else {
    yield put(updateCardPaymentInfoErrors(errors));
    yield put(updateCardPaymentInfoErrorsForBanner(bannerErrors));
  }

  return isFieldValid && isBannerValid;
}

// #genai Extracted to reduce cognitive complexity of submitBillingInfoSaga.
// Validates payment info for both field-level errors and the validation banner, dispatches both,
// and returns true only when all validations pass.
// #genai - consent param is the already-resolved consent value: achConsent for ACH, ccConsent for card.
function* validateAndDispatchPaymentErrors(paymentInfo, consent, isAch, isCard, transientTokenData, t) {
  const paymentInfoForValidation = {
    ...paymentInfo,
    paymentMethod: isAch ? PAYMENT_METHOD_OPTION_IDS.Ach : PAYMENT_METHOD_OPTION_IDS.Card,
  };

  const errors = validatePaymentInfo(paymentInfoForValidation, t, transientTokenData || {});
  // #genai Inline consent subtext is always generic "This field is required"; banner stays specific.
  errors.ccConsent = consent ? '' : t(getConsentFieldKey());
  if (!consent) errors.isError = true;
  const isFieldValid = !errors.isError;
  delete errors.isError;
  yield put(updatePaymentInfoErrors(errors));

  const bannerErrors = validatePaymentInfoForBanner(paymentInfoForValidation, t, transientTokenData || {});
  // #genai ACH consent banner copy varies by phase (Terms & Conditions in Phase 2); card keeps its own.
  const consentBannerKey = getConsentBannerKey(isAch, transientTokenData?.isPhase2Ach);
  bannerErrors.ccConsent = consent ? '' : t(consentBannerKey);
  if (!consent) bannerErrors.isError = true;
  const isBannerValid = !bannerErrors.isError;
  delete bannerErrors.isError;
  yield put(updatePaymentInfoErrorsForBanner(bannerErrors));

  return isFieldValid && isBannerValid;
}

// #genai Extracted to reduce cognitive complexity of submitBillingInfoSaga.
// Builds the redux-saga call effect for the appropriate billing service (ACH or card).
// Parameters are grouped to stay within the 7-parameter limit (javascript:S107).
const buildBillingEffect = (
  paymentDirty,
  isAch,
  isCard,
  paymentData,
  billingContext,
  isToggleUser = false, // #genai
) => {
  const {
    paymentInfo, updatedAddressInfo, transientToken, isACHDefault, consent, isSwitchOnly,
    isPhase2AchSave, isPhase2CardSave, isCardDefault,
  } = paymentData;
  const { achInfo, billingInformationSnapshot } = billingContext;

  if (paymentDirty && isAch) {
    // #genai Switch-only save (no ACH field edits, no consent change): send minimal body
    // to flip the default to ACH without touching the saved bank account details.
    const billPayerId = achInfo?.billPayerId || billingInformationSnapshot?.billPayerId;
    if (isSwitchOnly) {
      return call(updateBankAccountInfoService, { billPayerId, isACHDefault: true });
    }
    // #genai Phase 2 Add-ACH respects the user's "Make default" checkbox (§12.8). Phase 1 toggle
    // saves keep the legacy "saving via toggle sets ACH as default" behaviour.
    let achDefaultFlag;
    if (isPhase2AchSave) {
      achDefaultFlag = Boolean(isACHDefault);
    } else if (isToggleUser) {
      achDefaultFlag = true;
    } else {
      achDefaultFlag = isACHDefault;
    }
    const bankAccountRequestBody = getBankAccountInfoRequestBody(
      paymentInfo,
      updatedAddressInfo,
      transientToken,
      achDefaultFlag,
      consent,
      billPayerId,
    );
    return call(updateBankAccountInfoService, bankAccountRequestBody);
  }
  if (paymentDirty && isCard) {
    // #genai Phase 2 Add-Card sends the full §2.6 body (per-card billingXxx + companyName +
    // isDefault) and, per the guardrail, does NOT call the account-level address-information API.
    if (isPhase2CardSave) {
      return call(updateBillPayerInfoService, getPhase2CardRequestBody(paymentInfo, updatedAddressInfo, transientToken, isCardDefault, consent));
    }
    const cardData = getCardDataFromPaymentInfo(paymentInfo);
    if (transientToken) cardData.transientToken = transientToken;
    return call(updateBillPayerInfoService, { cardData, saveCard: consent });
  }
  return null;
};

// #genai Extracted from handleUpdateResponses to reduce cognitive complexity.
function* applyAchUpdateToState(paymentInfo, billingUpdateResponse, isToggleUser) {
  const updatedAchInfo = billingUpdateResponse.json || {};
  const resetAchPaymentInfo = updatedAchInfo.accountSuffix
    ? {
      firstName: updatedAchInfo.firstName || paymentInfo.firstName,
      lastName: updatedAchInfo.lastName || paymentInfo.lastName,
      accountType: updatedAchInfo.accountType || paymentInfo.accountType,
      routingNumber: ACH_SAVED_ROUTING_MASK,
      accountNumber: updatedAchInfo.accountSuffix,
      accountNumberConfirm: updatedAchInfo.accountSuffix,
    }
    : {};
  if (isToggleUser) {
    yield put(updateAchPaymentInfo({ ...paymentInfo, ...resetAchPaymentInfo, isUpdated: false })); // #genai
  } else {
    yield put(updatePaymentInfo({ ...paymentInfo, ...resetAchPaymentInfo, isUpdated: false }));
  }
  yield put(loadAchInfo(updatedAchInfo));
}

// #genai Extracted to reduce cognitive complexity of submitBillingInfoSaga.
// Handles address and billing service responses — throws on failure, dispatches state updates on success.
// updateContext groups addressDirty/paymentDirty/isAch/isCard to satisfy the max-params rule (javascript:S107).
function* handleUpdateResponses(
  { // #genai grouped context
    addressDirty,
    paymentDirty,
    isAch,
    isCard,
  },
  addressUpdateResponse,
  billingUpdateResponse,
  paymentInfo,
  isToggleUser = false, // #genai
) {
  if (addressDirty && !addressUpdateResponse.ok) {
    // #genai Guard against non-error 5xx bodies that omit errorDescription/errorCode
    const error = new Error(addressUpdateResponse.json?.errorDescription || 'Address update failed');
    error.errorCode = addressUpdateResponse.json?.errorCode;
    error.errorType = addressUpdateResponse.json?.errorType;
    throw error;
  }
  if (addressUpdateResponse?.ok) {
    yield put(updateAddressInfoFields(addressSelector(addressUpdateResponse.json)));
  }

  if (paymentDirty && billingUpdateResponse && !billingUpdateResponse.ok) {
    // #genai Guard against non-error 5xx bodies that omit errorDescription/errorCode
    const error = new Error(billingUpdateResponse.json?.errorDescription || 'Payment update failed');
    error.errorCode = billingUpdateResponse.json?.errorCode;
    // #genai Phase 2 — carry the backend errorType (guide §4 branches on errorType, not errorCode)
    // plus the orphan supportReference (503 PersistenceFailedAfterTokenize) for the support-contact copy.
    error.errorType = billingUpdateResponse.json?.errorType;
    error.supportReference = billingUpdateResponse.json?.supportReference;
    throw error;
  }

  if (paymentDirty && isCard && billingUpdateResponse?.ok) {
    const paymentInfoFromService = paymentInfoSelector(billingUpdateResponse.json);
    if (isToggleUser) {
      yield put(updateCardPaymentInfo({ ...paymentInfo, ...paymentInfoFromService, isUpdated: false })); // #genai
    } else {
      yield put(updatePaymentInfo({ ...paymentInfo, ...paymentInfoFromService, isUpdated: false }));
    }
    yield put(updateCreditCardConsent(billingUpdateResponse.json.ccConsent));
    return;
  }

  if (paymentDirty && isAch && billingUpdateResponse?.ok) {
    yield call(applyAchUpdateToState, paymentInfo, billingUpdateResponse, isToggleUser); // #genai
  }
}

// #genai Extracted from submitToggleUserBillingInfoSaga to reduce cognitive complexity
// (SonarQube S3776). Runs the Phase 2 Add-payment address validation and dispatches the
// resulting per-field errors + display flip. Returns the isValid flag; for non-Phase-2
// callers, uses the touch-based every-empty check on the existing addressInfoErrors slice.
function* validateAddressForSubmit({
  isPhase2AddSave, addressDirty, addressInfo, addressInfoErrors, t,
}) {
  if (isPhase2AddSave) {
    const {
      errors: phase2Errors,
      banner: phase2Banner,
      isValid: phase2AddressValid,
    } = getPhase2AddressValidationErrors(addressInfo, t);
    // #genai Dispatch all per-field error puts in parallel via `all([...])` so we avoid an
    // indexed for-loop (project lint rule `no-restricted-syntax`). The put effect is
    // fire-and-forget, so ordering here doesn't matter — the reducer merges each independently.
    const errorPuts = Object.keys(phase2Errors).map((fid) => (
      put(updateAddressInfoErrors(fid, phase2Errors[fid], phase2Banner[fid]))
    ));
    yield all(errorPuts);
    yield put(updateAddressValidationErrorsForDisplay());
    return phase2AddressValid;
  }
  if (addressDirty) {
    yield put(updateAddressValidationErrorsForDisplay());
    return Object.values(addressInfoErrors).every((val) => val === '');
  }
  return true;
}

// #genai Extracted from submitToggleUserBillingInfoSaga to reduce cognitive complexity.
// Validates payment for the no-dirty-changes case and dispatches complete or validation-error.
function* handleNoDirtyToggleCase(activePaymentInfo, consent, isAch, transientTokenData, t) {
  const isPaymentInfoValid = yield call(
    validateAndDispatchTogglePaymentErrors,
    activePaymentInfo,
    consent,
    isAch,
    transientTokenData,
    t,
  );
  if (isPaymentInfoValid) {
    yield put(submitBillingInfoComplete());
  } else {
    yield put(submitBillingInfoValidationError());
  }
  // #genai Mirror the non-toggle path — scroll to the top so the success/validation banner
  // is visible to users who triggered Save from lower on the form.
  scrollToTop();
}

// #genai Extracted from submitBillingInfoSaga to reduce cognitive complexity.
// Handles the full save flow for ACH-eligible (toggle) users.
function* submitToggleUserBillingInfoSaga(billingState, billingInformationSnapshot, transientTokenData, t) {
  const {
    cardPaymentInfo, achPaymentInfo, selectedPaymentMethod, savedSelectedPaymentMethod,
    ccConsent, achConsent, isAddressInfoUpdated, addressInfo, addressInfoErrors,
    updatedCCConsent, updatedACHConsent, achInfo, isACHDefault,
  } = billingState;

  const isAch = selectedPaymentMethod === PAYMENT_METHOD_OPTION_IDS.Ach;
  const isCard = selectedPaymentMethod === PAYMENT_METHOD_OPTION_IDS.Card;
  const activePaymentInfo = isAch ? achPaymentInfo : cardPaymentInfo;
  const consent = isAch ? achConsent : ccConsent;
  const consentUpdated = isAch ? updatedACHConsent : updatedCCConsent; // #genai
  const paymentMethodSwitched = selectedPaymentMethod !== savedSelectedPaymentMethod;
  const paymentDirty = activePaymentInfo.isUpdated || consentUpdated || paymentMethodSwitched;
  const addressDirty = isAddressInfoUpdated;
  const isAddressOnlySave = addressDirty && !paymentDirty;
  // #genai True when paymentDirty is true ONLY because the user toggled methods —
  // no field edits and no consent toggle on the active method.
  const isSwitchOnly = paymentMethodSwitched && !activePaymentInfo.isUpdated && !consentUpdated;

  if (!addressDirty && !paymentDirty) {
    yield call(handleNoDirtyToggleCase, activePaymentInfo, consent, isAch, transientTokenData, t);
    return;
  }

  const { transientToken } = transientTokenData || {};

  let isPaymentInfoValid = true;
  if (!isAddressOnlySave) {
    isPaymentInfoValid = yield call(
      validateAndDispatchTogglePaymentErrors,
      activePaymentInfo,
      consent,
      isAch,
      transientTokenData,
      t,
    );
  }

  // #genai Phase 2 Add-ACH / Add-Card (Figma 885:10004) open an empty billing form (no
  // LOAD_BILLING_INFO on these routes), so the user may click Save without ever touching Billing
  // Information — leaving addressInfoErrors empty and the "every val === ''" check falsely happy.
  // For these dedicated add pages, force full required-field validation on every submit so the
  // ValidationMessageBanner surfaces missing Company / Address / City / Zip / etc. AchPaymentInfo
  // First/Last name are already covered above by validateAndDispatchTogglePaymentErrors. Phase 1
  // (no isPhase2Ach / isPhase2Card flag) and every non-add flow keep the original touch-based
  // gate untouched.
  const isPhase2AchSave = Boolean(transientTokenData?.isPhase2Ach) && isAch;
  const isPhase2CardSave = Boolean(transientTokenData?.isPhase2Card) && isCard;
  const isPhase2AddSave = isPhase2AchSave || isPhase2CardSave;

  const isAddressInfoValid = yield call(validateAddressForSubmit, {
    isPhase2AddSave, addressDirty, addressInfo, addressInfoErrors, t,
  });

  if (!isPaymentInfoValid || !isAddressInfoValid) {
    yield put(submitBillingInfoValidationError());
    // #genai Scroll AFTER errors are dispatched so the banner is rendered when the viewport
    // reaches the top — mirrors the non-toggle path; toggle users would otherwise miss the
    // banner when Save is clicked from lower on the form.
    scrollToTop();
    return;
  }

  const updatedAddressInfo = {
    ...addressInfo,
    firstName: activePaymentInfo.firstName,
    lastName: activePaymentInfo.lastName,
  };
  yield put(submitBillingInfoStart());

  const billingEffect = buildBillingEffect(
    paymentDirty,
    isAch,
    isCard,
    {
      paymentInfo: activePaymentInfo,
      updatedAddressInfo,
      transientToken,
      isACHDefault,
      consent,
      isSwitchOnly,
      isPhase2AchSave,
      isPhase2CardSave,
      isCardDefault: Boolean(transientTokenData?.isDefault),
    },
    { achInfo, billingInformationSnapshot },
    true,
  );
  // #genai Guardrail (MYEV_UI_INTEGRATION_GUIDE 2 §2.6): the Phase 2 Add-Card / Add-ACH flows must
  // NOT call the account-level address-information API — it writes BillPayer.BillingAddressID
  // unconditionally and would overwrite the customer's main NetSuite bill-to for a non-default add.
  // The per-method address rides inside the card body (billingXxx) / bank body (billTo) instead.
  // Phase 1 saves keep the existing parallel address PUT.
  const shouldPutAddress = addressDirty && !isPhase2AddSave;
  const [addressUpdateResponse, billingUpdateResponse] = yield all([
    shouldPutAddress ? call(updateAddressInfoService, ADDRESS_TYPES.BillToAddress, updatedAddressInfo) : null,
    billingEffect,
  ]);

  yield call(
    handleUpdateResponses,
    {
      addressDirty: shouldPutAddress, paymentDirty, isAch, isCard,
    },
    addressUpdateResponse,
    billingUpdateResponse,
    activePaymentInfo,
    true,
  );
  yield put(submitBillingInfoComplete());
  // #genai Scroll so the success banner at the top is visible (mirrors non-toggle path).
  // Backend failures throw out of handleUpdateResponses into the outer saga's catch block,
  // which already calls scrollToTop, so this only fires on the success path.
  scrollToTop();
}

export function* submitBillingInfoSaga({ transientTokenData }) {
  trackUserEvent(USER_EVENTS.SAVE_BILLING_INFO);
  yield put(resetBillingSubmission());
  const { t } = i18n;
  try {
    const billingState = yield select(({ billingInformation }) => billingInformation);
    const billingInformationSnapshot = yield select(({ userData }) => userData.billingInformation || {});
    const isAchEligible = yield select(isAchEligibleSelector);

    if (isAchEligible) { // #genai Toggle user flow fully handled by the extracted saga.
      yield call(submitToggleUserBillingInfoSaga, billingState, billingInformationSnapshot, transientTokenData, t);
      return;
    }

    const {
      paymentInfo, ccConsent, achConsent, isAddressInfoUpdated, addressInfo,
      addressInfoErrors, updatedCCConsent, updatedACHConsent, achInfo, isACHDefault, updatedACHDefault,
    } = billingState;

    const isAch = isAchEligible;
    const isCard = billingInformationSnapshot?.billingType === BillingType.CREDIT_CARD;

    if (!isAddressInfoUpdated && !paymentInfo.isUpdated && !updatedCCConsent && !updatedACHConsent && !updatedACHDefault) {
      yield put(submitBillingInfoComplete());
      return;
    }

    const { transientToken } = transientTokenData || {};
    const consent = isAch ? achConsent : ccConsent;
    const paymentDirty = paymentInfo.isUpdated || updatedCCConsent || updatedACHConsent || updatedACHDefault;
    const addressDirty = isAddressInfoUpdated;

    let isPaymentInfoValid = true;
    if (paymentDirty && (isAch || isCard)) {
      isPaymentInfoValid = yield call(validateAndDispatchPaymentErrors, paymentInfo, consent, isAch, isCard, transientTokenData, t);
    }

    let isAddressInfoValid = true;
    if (addressDirty) {
      isAddressInfoValid = Object.values(addressInfoErrors).every((val) => val === '');
      yield put(updateAddressValidationErrorsForDisplay());
    }

    if (!isPaymentInfoValid || !isAddressInfoValid) {
      yield put(submitBillingInfoValidationError());
      // #genai Scroll AFTER errors are dispatched so the banner is already rendered when the
      // viewport reaches the top — guarantees the user sees the validation messages.
      scrollToTop();
      return;
    }

    const updatedAddressInfo = { ...addressInfo, firstName: paymentInfo.firstName, lastName: paymentInfo.lastName };
    yield put(submitBillingInfoStart());

    const billingEffect = buildBillingEffect(
      paymentDirty,
      isAch,
      isCard,
      {
        paymentInfo, updatedAddressInfo, transientToken, isACHDefault, consent,
      },
      { achInfo, billingInformationSnapshot },
    );
    const [addressUpdateResponse, billingUpdateResponse] = yield all([
      addressDirty ? call(updateAddressInfoService, ADDRESS_TYPES.BillToAddress, updatedAddressInfo) : null,
      billingEffect,
    ]);

    yield call(
      handleUpdateResponses,
      {
        addressDirty, paymentDirty, isAch, isCard,
      },
      addressUpdateResponse,
      billingUpdateResponse,
      paymentInfo,
    );
    yield put(submitBillingInfoComplete());
    // #genai Scroll so the success banner at top is visible after a successful save.
    scrollToTop();
  } catch (error) {
    logError({
      message: 'Error in submitBillingInfoSaga',
      error: error.message,
      errorCode: error.errorCode,
    });
    // #genai Always dispatch a truthy errorMessage so the banner correctly renders as error
    // (severity: error). Without this, a thrown Error with an undefined message would set
    // errorMessage to falsy, causing the success banner to show on real failures (e.g. 503).
    // #genai Pass errorCode through so the banner can look up the curated FE message for
    // known ev-ecomm-orchestration-service errors (see BankAccountInfoErrors.js).
    yield put(submitBillingInfoError(
      error.message || 'Failed to save billing information',
      error.errorCode,
      error.errorType,
      error.supportReference,
    ));
    // #genai Scroll so the error banner at top is visible after a backend failure.
    scrollToTop();
  }
}

// #genai Phase 2 — reads the error envelope { errorType } off a non-2xx service response.
const readErrorType = (json) => json?.errorType || 'Unknown';

// #genai Phase 2 — GET /v1/payment-methods. The component only dispatches this when the Phase 2
// gate is active, so there is no client-side short-circuit here. Per the Phase 2 integration
// (no fallback to Phase 1), a 404 (flag OFF / feature unavailable server-side) is surfaced as an
// error state so the list shows a retry banner rather than silently downgrading.
export function* loadPaymentMethodsSaga() {
  yield put(loadPaymentMethodsStart());
  try {
    const response = yield call(getPaymentMethodsService);
    if (!response.ok) {
      yield put(loadPaymentMethodsError({ httpStatus: response.status, errorType: readErrorType(response.json) }));
      return;
    }
    yield put(loadPaymentMethodsSuccess(response.json?.paymentMethods || []));
  } catch (error) {
    logError({ message: 'Error in loadPaymentMethodsSaga', error: error.message });
    yield put(loadPaymentMethodsError({ httpStatus: 0, errorType: 'Unknown' }));
  }
}

// #genai Phase 2 — PUT set-default. Optimistic flip already applied in the reducer on
// SET_PAYMENT_METHOD_DEFAULT; here we confirm or roll back based on the service result.
export function* setPaymentMethodDefaultSaga({ method }) {
  try {
    const response = yield call(setPaymentMethodDefaultService, method);
    if (!response.ok) {
      yield put(setPaymentMethodDefaultError(method, { httpStatus: response.status, errorType: readErrorType(response.json) }));
      return;
    }
    yield put(setPaymentMethodDefaultSuccess(method));
  } catch (error) {
    logError({ message: 'Error in setPaymentMethodDefaultSaga', error: error.message });
    yield put(setPaymentMethodDefaultError(method, { httpStatus: 0, errorType: 'Unknown' }));
  }
}

// #genai Phase 2 — PUT remove. The reducer only marks the row pending on REMOVE_PAYMENT_METHOD and
// deletes it on SUCCESS, so every failure path below leaves the list exactly as the user saw it; a
// 409 CannotRemoveDefault blocks the action so the UI can prompt them to pick a new default first.
export function* removePaymentMethodSaga({ method }) {
  try {
    const response = yield call(removePaymentMethodService, method);
    if (response.status === CLIENT_ERROR_RESPONSES.STATUS_CONFLICT) {
      yield put(removePaymentMethodBlockedDefault(method));
      return;
    }
    if (!response.ok) {
      // #genai Pass errorDescription so the list Banner can show the BFF user-facing text (e.g. 15847).
      yield put(removePaymentMethodError(method, {
        httpStatus: response.status,
        errorType: readErrorType(response.json),
        errorDescription: response.json?.errorDescription,
        errorCode: response.json?.errorCode,
      }));
      return;
    }
    yield put(removePaymentMethodSuccess(method));
  } catch (error) {
    logError({ message: 'Error in removePaymentMethodSaga', error: error.message });
    yield put(removePaymentMethodError(method, { httpStatus: 0, errorType: 'Unknown' }));
  }
}
